"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { BookOpen, PenLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";

/**
 * The single reader↔writer "two-way door".
 *
 * One component, dropped into the SAME navbar slot on every surface. It decides
 * its own direction from the current route + role, so every navbar stays
 * identical and there is exactly one switch per surface:
 *   - author surfaces (/editor, /wiki) → outlined "Read" pill → /browse
 *   - reader surfaces, author/admin     → filled "Write" pill  → /editor
 *   - reader surfaces, reader/anon      → filled "Write" pill  → /become-a-writer
 *
 * Role is personalization only — middleware still enforces access, so a stale
 * or forged role just yields a redirect, never unauthorized access.
 */
export function SurfaceSwitch({ className }: { className?: string }) {
    const { profile, loading } = useAuth();
    const pathname = usePathname() ?? "";
    const inAuthorArea =
        pathname.startsWith("/editor") || pathname.startsWith("/wiki");

    // Author surfaces: the "Read" door needs no role check — everyone in the
    // editor/wiki is an author (middleware-enforced) — and doesn't depend on
    // the async profile, so render it immediately with no loading skeleton.
    if (inAuthorArea) {
        return (
            <Link
                href="/browse"
                className={cn(
                    // Theme-aware `primary` (not the fixed-dark `brand`): the
                    // editor header is dark by default, where brand #7C3AED only
                    // hits ~2.7:1 contrast. `primary` brightens to #A29BFE in
                    // dark → legible in both themes. Outlined (not filled) so it
                    // never competes with the editor's primary actions
                    // (New Story / Publish).
                    "flex items-center gap-1.5 rounded-full border border-primary/40 px-3.5 py-1 text-[13px] font-bold text-primary transition-colors hover:bg-primary/10",
                    className
                )}
            >
                <BookOpen className="size-3.5" aria-hidden />
                Read
            </Link>
        );
    }

    // Reader surfaces: content depends on role. Reserve the slot while auth
    // resolves so the CTA doesn't flash in and then swap to "Write" for authors
    // (layout stays stable either way).
    if (loading) {
        return (
            <span
                aria-hidden="true"
                className={cn(
                    "h-7 w-20 animate-pulse rounded-full bg-navbar-text-faint/15",
                    className
                )}
            />
        );
    }

    // Always "Write" — authors land in their editor, readers/anon route through
    // the writer-upgrade page. Same label either way so the door reads the same
    // to everyone; middleware still gates /editor for non-authors.
    const isAuthor = profile?.canAuthor ?? false;

    return (
        <Link
            href={isAuthor ? "/editor" : "/become-a-writer"}
            className={cn(
                "flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1 text-[13px] font-bold text-white shadow-md shadow-brand/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand/40",
                className
            )}
        >
            <PenLine className="size-3.5" aria-hidden />
            Write
        </Link>
    );
}
